/**
 * MediaUploader.jsx
 * File: src/components/UI/MediaUploader.jsx (BROKER ADMIN)
 *
 * Reusable media picker component. Supports:
 * - Drag & drop upload
 * - Click to upload
 * - Browse existing media library
 * - Manual URL input as fallback
 * - Preview for images/videos
 *
 * Usage:
 *   <MediaUploader
 *     value={imageUrl}
 *     onChange={(url) => setImageUrl(url)}
 *     accept="image"          // "image" | "video" | "document" | "all" 
 *     label="Hero Image"      // optional
 *   />
 */

import { useState, useRef, useCallback } from 'react';
import {
  Upload, X, Image, Film, FileText, Trash2, Search,
  Link2, FolderOpen, Loader, Check, ChevronDown
} from 'lucide-react';
import { mediaAPI } from '../../services/api';

const CATEGORY_ICON = {
  image: Image,
  video: Film,
  document: FileText,
};

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function MediaUploader({ value, onChange, accept = 'all', label }) {
  const [mode, setMode] = useState(null); // null | 'upload' | 'library' | 'url'
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libSearch, setLibSearch] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const acceptMime = accept === 'image' ? 'image/*'
    : accept === 'video' ? 'video/*'
    : accept === 'document' ? '.pdf,.doc,.docx,.txt'
    : 'image/*,video/*,.pdf,.doc,.docx,.txt';

  // ── Upload handler ──────────────────────────────────────────────────────
  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await mediaAPI.upload(formData);
      const data = res.data || res;
      if (data.success) {
        onChange(data.data.url);
        setMode(null);
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  // ── Library loader ──────────────────────────────────────────────────────
  const loadLibrary = async () => {
    setLibLoading(true);
    try {
      const category = accept === 'all' ? undefined : accept;
      const res = await mediaAPI.list({ category, limit: 100, search: libSearch || undefined });
      const data = res.data || res;
      if (data.success) setLibrary(data.data || []);
    } catch { setLibrary([]); }
    finally { setLibLoading(false); }
  };

  const openLibrary = () => {
    setMode('library');
    loadLibrary();
  };

  const deleteMedia = async (id, e) => {
    e.stopPropagation();
    try {
      await mediaAPI.delete(id);
      setLibrary(prev => prev.filter(f => f.id !== id));
      if (library.find(f => f.id === id)?.url === value) onChange('');
    } catch { /* ignore */ }
  };

  // ── Determine preview type ──────────────────────────────────────────────
  const isImage = value && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(value);
  const isVideo = value && /\.(mp4|webm|mov)$/i.test(value);

  // ── Main render ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-2">
      {/* Current value preview */}
      {value ? (
        <div className="relative group rounded-xl overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
          {isImage && (
            <img src={value} alt="" className="w-full h-48 object-cover" />
          )}
          {isVideo && (
            <video src={value} className="w-full h-48 object-cover" muted />
          )}
          {!isImage && !isVideo && (
            <div className="flex items-center gap-3 p-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
              <FileText className="w-5 h-5 shrink-0" style={{ color: 'var(--text-muted)' }} />
              <span className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>{value}</span>
            </div>
          )}

          {/* Overlay actions */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <button onClick={() => setMode('upload')}
              className="px-3 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-semibold hover:bg-white/30 transition-colors">
              Replace
            </button>
            <button onClick={() => onChange('')}
              className="px-3 py-2 rounded-lg bg-red-500/70 backdrop-blur-sm text-white text-xs font-semibold hover:bg-red-500/90 transition-colors">
              Remove
            </button>
          </div>
        </div>
      ) : (
        /* Empty state — action buttons */
        <div className="flex gap-2">
          <button onClick={() => setMode('upload')}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed text-sm font-semibold transition-all hover:border-[var(--accent-primary)]"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
            <Upload className="w-4 h-4" /> Upload
          </button>
          <button onClick={openLibrary}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed text-sm font-semibold transition-all hover:border-[var(--accent-primary)]"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
            <FolderOpen className="w-4 h-4" /> Library
          </button>
          <button onClick={() => { setMode('url'); setUrlInput(value || ''); }}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed text-sm font-semibold transition-all hover:border-[var(--accent-primary)]"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
            <Link2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* URL display when value exists */}
      {value && (
        <div className="flex items-center gap-2">
          <input value={value} readOnly
            className="flex-1 px-2 py-1 rounded text-xs font-mono truncate border outline-none"
            style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)', color: 'var(--text-muted)' }} />
          <button onClick={openLibrary} className="p-1.5 rounded hover:bg-[var(--bg-primary)]" title="Browse library">
            <FolderOpen className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
          </button>
          <button onClick={() => { setMode('url'); setUrlInput(value || ''); }} className="p-1.5 rounded hover:bg-[var(--bg-primary)]" title="Edit URL">
            <Link2 className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>
      )}

      {/* ═══════════ UPLOAD MODAL ═══════════ */}
      {mode === 'upload' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => !uploading && setMode(null)}>
          <div className="w-full max-w-md rounded-2xl border p-6" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--text-primary)' }}>Upload File</h3>
              <button onClick={() => setMode(null)} className="p-1 rounded hover:bg-[var(--bg-primary)]"><X className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} /></button>
            </div>

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className={`rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-all ${dragOver ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)]/5' : ''}`}
              style={{ borderColor: dragOver ? 'var(--accent-primary)' : 'var(--border-color)' }}>

              {uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Uploading...</p>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                  <p className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                    Drop file here or click to browse
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Max 20MB · Images, Videos, Documents
                  </p>
                </>
              )}

              <input ref={fileRef} type="file" accept={acceptMime} onChange={handleFileInput} className="hidden" />
            </div>

            {error && <p className="text-sm text-red-400 mt-3 text-center">{error}</p>}
          </div>
        </div>
      )}

      {/* ═══════════ LIBRARY MODAL ═══════════ */}
      {mode === 'library' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setMode(null)}>
          <div className="w-full max-w-2xl rounded-2xl border max-h-[80vh] flex flex-col" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="font-bold" style={{ color: 'var(--text-primary)' }}>Media Library</h3>
              <div className="flex items-center gap-2">
                <button onClick={() => setMode('upload')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                  style={{ backgroundColor: 'var(--accent-primary)' }}>
                  <Upload className="w-3.5 h-3.5" /> Upload New
                </button>
                <button onClick={() => setMode(null)} className="p-1 rounded hover:bg-[var(--bg-primary)]"><X className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} /></button>
              </div>
            </div>

            {/* Search */}
            <div className="px-4 pt-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                <input
                  value={libSearch}
                  onChange={e => setLibSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadLibrary()}
                  placeholder="Search files..."
                  className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border outline-none"
                  style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>

            {/* Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {libLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="w-6 h-6 animate-spin" style={{ color: 'var(--accent-primary)' }} />
                </div>
              ) : library.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No files found</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {library.map(file => {
                    const isSelected = value === file.url;
                    const isImg = file.category === 'image';
                    const IconComp = CATEGORY_ICON[file.category] || FileText;

                    return (
                      <div key={file.id}
                        onClick={() => { onChange(file.url); setMode(null); }}
                        className="relative group rounded-xl overflow-hidden border cursor-pointer transition-all hover:shadow-md"
                        style={{
                          borderColor: isSelected ? 'var(--accent-primary)' : 'var(--border-color)',
                          borderWidth: isSelected ? '2px' : '1px',
                        }}>

                        {isImg ? (
                          <img src={file.url} alt={file.original_name} className="w-full h-24 object-cover" />
                        ) : (
                          <div className="w-full h-24 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
                            <IconComp className="w-8 h-8" style={{ color: 'var(--text-muted)' }} />
                          </div>
                        )}

                        {/* Filename */}
                        <div className="px-2 py-1.5">
                          <p className="text-[10px] truncate" style={{ color: 'var(--text-secondary)' }}>{file.original_name}</p>
                          <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{formatFileSize(file.file_size)}</p>
                        </div>

                        {/* Selected check */}
                        {isSelected && (
                          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--accent-primary)' }}>
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}

                        {/* Delete button */}
                        <button onClick={(e) => deleteMedia(file.id, e)}
                          className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-red-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-2.5 h-2.5 text-white" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ URL INPUT MODAL ═══════════ */}
      {mode === 'url' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setMode(null)}>
          <div className="w-full max-w-md rounded-2xl border p-6" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--text-primary)' }}>Enter URL</h3>
              <button onClick={() => setMode(null)} className="p-1 rounded hover:bg-[var(--bg-primary)]"><X className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} /></button>
            </div>
            <input value={urlInput} onChange={e => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="w-full px-3 py-2.5 rounded-lg text-sm border outline-none mb-4"
              style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              onKeyDown={e => { if (e.key === 'Enter') { onChange(urlInput); setMode(null); } }}
              autoFocus />
            <div className="flex justify-end gap-2">
              <button onClick={() => setMode(null)} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Cancel</button>
              <button onClick={() => { onChange(urlInput); setMode(null); }}
                className="px-4 py-2 rounded-lg text-sm font-bold text-white"
                style={{ backgroundColor: 'var(--accent-primary)' }}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}








